import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Typography } from "@tento-ui/ui/typography";
import { Avatar } from "@tento-ui/components/avatar";
import { Facepile } from "@tento-ui/components/facepile";
import { IconButton } from "@tento-ui/components/icon-button";
import { Tooltip } from "@tento-ui/components/tooltip/tooltip";

/** Inlined rather than imported from `icons/three-dots.svg`. The storybook
 * harness bundles a card with bun, which hands back a PATH for an `.svg`
 * import instead of its markup; `LonaIcons.$svg` then finds no element and
 * the whole card dies. A component that cannot render in its own storybook
 * cannot be reviewed, so this one carries its glyph. */
const DETAILS_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g fill="currentColor"><circle cx="3.4" cy="8" r="1.25"/><circle cx="8" cy="8" r="1.25"/><circle cx="12.6" cy="8" r="1.25"/></g></svg>`;

/** `turn.bar` — the faint trailing bar that closes a turn.
 *
 * This replaces an attribution HEADER. A header charges every reader the
 * same attention for a question few of them have; reading a transcript you
 * want the reply, and you go looking for which lane answered only when
 * something is wrong. Every mainstream agent surface has already made this
 * move — the controls sit under the message, quiet until you look.
 *
 * Two things and no others: icon actions, which the caller names and
 * handles, and metadata, which is DISCLOSED WITHOUT DISPLACING ANYTHING.
 * The metadata rides an anchored `std-tooltip` on the details button, so
 * revealing it costs no layout — which matters more here than anywhere
 * else in the family, because these rows live in a virtualized transcript
 * where an expanding row invalidates its own measured height and shoves
 * the scrollback under the reader's cursor.
 *
 * Actions carry their own icon rather than choosing from a vocabulary this
 * component owns: every surface has different verbs, and a closed list
 * would be re-litigated by the first caller that wanted one more. The
 * label becomes the accessible name and the hover tooltip, so an icon
 * button is never unlabelled. */
@component({ name: "std-turn-bar" })
export class TurnBar extends LonaWebComponent {
  private $actions: HTMLElement = this.$("actions");
  private $faces: HTMLElement = this.$("faces");
  private $details: HTMLElement = this.$("details");
  private $detailsButton: IconButton = this.hydrate("details-button");
  private $meta: HTMLElement = this.$("meta");

  static makeWith(props: TurnBar.Props): TurnBar {
    return (TurnBar.make() as TurnBar).bind(props);
  }

  bind(props: TurnBar.Props): this {
    const actions = props.actions ?? [];
    const meta = props.meta ?? [];

    this.$actions.replaceChildren(
      ...actions.map((action) => {
        const $anchor = document.createElement("span");
        $anchor.className = "anchor";
        const $button = IconButton.make().bindIcon(action.icon, {
          sizePx: 22,
          squarish: true,
          ariaLabel: action.label,
        });
        // `addPointerEvent` assigns the `on*` properties rather than
        // stacking listeners, so rebinding a pooled row re-points it.
        if (props.onAction != null) {
          GESTURE_MANAGER.addPointerEvent($button, {
            onClick: () => props.onAction!(action.id),
          });
        }
        const $tip = Tooltip.make();
        $tip.setAttribute("position", "bottom");
        $tip.textContent = action.label;
        $anchor.append($button, $tip);
        return $anchor;
      }),
    );

    this.bindFaces(props);

    // No metadata, no button — an affordance that opens nothing is worse
    // than no affordance.
    this.$details.style.display = meta.length === 0 ? "none" : "";
    this.$detailsButton.bindIcon(DETAILS_ICON, {
      sizePx: 22,
      squarish: true,
      ariaLabel: "turn details",
    });
    this.$meta.replaceChildren(
      ...meta.flatMap(({ key, value }) => {
        const $key = document.createElement("span");
        $key.className = "meta-key";
        $key.textContent = key;
        const $value = document.createElement("span");
        $value.className = "meta-value";
        $value.textContent = value;
        return [$key, $value];
      }),
    );
    return this;
  }

  /** The facepile: one face per action the turn took.
   *
   * `std-facepile` and `std-avatar`, not shapes drawn here. The first draft of
   * this WAS drawn here — spans with a border-radius, a negative margin and a
   * ring in the page colour — which is a reimplementation of `std-facepile`
   * down to the ring token it already exposes as
   * `--std-facepile-border-color`. A second copy of a primitive is a fork that
   * drifts the first time either is touched, and it is why this family exists
   * at all.
   *
   * What the composition still owns is the MAPPING: an action's status
   * becomes an avatar's background, and its subject becomes the avatar's
   * tooltip and initial. Overlap is what makes the set read as one object
   * with a size rather than as a list to count, and colour is what makes a
   * failure findable without reading — a red face among green is located
   * before any label is parsed.
   *
   * Capped, because past a dozen the stack stops carrying a count and starts
   * carrying a width. The remainder becomes a number, honest about being a
   * number rather than pretending to still be a picture. */
  private static readonly FACE_CAP = 12;

  private static readonly FACE_TONE: Record<TurnBar.Face["tone"], string> = {
    pending: "var(--text-ter, #8b93a7)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
    failed: "var(--fg-red, #e5484d)",
    declined: "var(--fg-yellow, #d4a72c)",
  };

  /** The face's one character. A tool name or a path is what the reader is
   * looking for, so its first letter is the useful mark; the status is
   * already the colour and does not need saying twice. */
  private static faceInitial(label: string): string {
    const match = /[A-Za-z0-9]/.exec(label);
    return match == null ? "·" : match[0].toUpperCase();
  }

  private bindFaces(props: TurnBar.Props): void {
    const faces = props.faces ?? [];
    this.$faces.style.display = faces.length === 0 ? "none" : "";
    if (faces.length === 0) {
      this.$faces.replaceChildren();
      return;
    }
    const shown = faces.slice(0, TurnBar.FACE_CAP);
    const rest = faces.length - shown.length;

    const $pile = Facepile.make();
    $pile.setAttribute("size", "16");
    // The ring `std-facepile` cuts between stacked faces defaults to `--bg`,
    // which is the PAGE. The bar sits on the turn's own surface, so the ring
    // is pointed at that instead — otherwise the gaps between faces read as
    // holes punched through to the page behind.
    $pile.style.setProperty(
      "--std-facepile-border-color",
      "var(--bg-elevated, var(--bg, #fff))",
    );
    for (const face of shown) {
      $pile.append(Avatar.makeWithInitials({
        initials: TurnBar.faceInitial(face.label),
        backgroundColor: TurnBar.FACE_TONE[face.tone],
        color: "var(--text-sec-on-solid, #fff)",
        size: "16",
        // No drop shadow: the pile already separates its faces with a ring,
        // and a shadow under each one turns a flat stack into a pile of
        // chips at a size where the shadow is a fifth of the face.
        shadow: false,
        // No per-face tooltip, and this is a property of the PILE rather than
        // an oversight. `std-avatar` anchors its tooltip inside its own 16px
        // box, and the pile overlaps its faces by 8px — so a face's tooltip
        // paints under every face stacked after it and only the strip that
        // clears them survives, which reads as a stray underline rather than
        // as a label. Nothing here can fix that: a tooltip that must escape
        // its anchor and outrank later siblings is asking the overlap to stop
        // being an overlap. There is also nothing to hover — half of a 16px
        // circle is not a target — so the labels live one click away, in the
        // modal, which is what the pile is FOR.
      }));
    }

    const $button = document.createElement("button");
    $button.type = "button";
    $button.className = "facepile-button";
    // One label for one object. The accessible name is the whole point of the
    // control — a stack of coloured circles is nothing at all to a screen
    // reader — and the tooltip repeats it for the pointer, anchored to the
    // button, which is big enough to hover and has no sibling to hide behind.
    const failed = faces.filter((face) =>
      face.tone === "failed" || face.tone === "declined"
    ).length;
    const label = failed > 0
      ? `${faces.length} actions, ${failed} failed — open`
      : `${faces.length} actions — open`;
    $button.ariaLabel = label;
    $button.append($pile);
    if (rest > 0) {
      const $rest = Typography.small(`+${rest}`);
      // ADD to the typography class, never overwrite it: `className =` was
      // stripping `Typography.small`'s own class, so the count fell out of
      // the type ramp and its capheight trim — the one skin whose overflow
      // count ignored a typography-token override.
      $rest.classList.add("face-rest");
      $button.append($rest);
    }
    const $tip = Tooltip.make();
    $tip.setAttribute("position", "bottom");
    $tip.textContent = label;
    $button.append($tip);
    if (props.onFaces != null) {
      GESTURE_MANAGER.addPointerEvent($button, {
        onClick: () => props.onFaces!(),
      });
    }
    this.$faces.replaceChildren($button);
  }

  static $styles = [
    Tooltip.$style,
    css`
      /* The bar is a full-bleed row under the turn's content; without an
         explicit width a shrink-to-fit parent collapses it and the
         actions stop sitting where the reader expects them. */
      :host {
        display: block;
        width: 100%;
      }
      /* Everything sits left, on the content's own margin: this is a menu
         under the turn, not a right rail. A lone button stranded on the
         far right reads as an orphan rather than as a control belonging
         to the text above it. */
      #root {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 2px;
        min-height: 24px;
        /* Faint at rest — the turn's content is what the reader came for.
           Hover or keyboard focus brings it up to normal quiet. */
        opacity: 0.45;
        transition: opacity 120ms ease;
      }
      :host(:hover) #root,
      :host(:focus-within) #root {
        opacity: 1;
      }
      #actions {
        display: flex;
        align-items: center;
        gap: 2px;
        min-width: 0;
      }
      #faces {
        display: flex;
        align-items: center;
        min-width: 0;
      }
      /* The button is the only chrome this file draws — the stack, its
         overlap and the ring that separates the dots all come from
         std-facepile, which is why there are no .face rules here. */
      .facepile-button {
        /* Positioned so the pile's one tooltip anchors to the BUTTON and not
           to a face inside it, and overflow stated so nothing clips it — the
           two properties the per-face tooltips could never have. */
        position: relative;
        overflow: visible;
        display: flex;
        align-items: center;
        gap: 0;
        margin-left: 4px;
        padding: 3px 6px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        cursor: pointer;
      }
      .facepile-button:hover {
        background: var(--bg-hover, color-mix(in srgb, var(--text-pri) 6%, transparent));
      }
      .face-rest {
        flex: none;
        margin-left: 7px;
        color: var(--text-ter, #8b93a7);
        font-family: var(--font-mono, ui-monospace, monospace);
        /* Through the fine-print slot, not literal pixels: the ramp reads
           --font-size (a capheight slot) and computes the real size and
           trim, so a look's typography override reaches this count the
           same way it reaches every other fine label in the skin. */
        --font-size: var(--turn-fine-size, var(--text-8px));
      }
      /* Positioned so the tooltip anchors to the button and not to the
         row. The overflow is stated rather than left implied because a
         clipping ancestor is the usual way an anchored tooltip
         disappears. */
      .anchor {
        position: relative;
        display: inline-flex;
        overflow: visible;
      }
      std-icon-button {
        --std-icon-button-color: var(--text-ter, #8b93a7);
        --std-icon-button-padding: 9px;
      }
      /* The tooltip supplies the padding and the surface; the grid inside
         it only has to line the keys up. */
      #meta {
        display: grid;
        grid-template-columns: max-content minmax(0, 1fr);
        gap: 2px 12px;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        /* Facts about the turn — the fine register, and a ratio rather
           than a box because a grid cell's value may wrap. */
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.4;
        text-align: start;
      }
      #details std-tooltip {
        max-width: 340px;
      }
      .meta-key {
        opacity: 0.65;
        white-space: nowrap;
      }
      .meta-value {
        min-width: 0;
        overflow-wrap: anywhere;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <span id="actions"></span>
      <span id="details" class="anchor">
        <std-icon-button id="details-button"></std-icon-button>
        <std-tooltip position="bottom">
          <span id="meta"></span>
        </std-tooltip>
      </span>
      <span id="faces"></span>
    </div>
  `;
}

export namespace TurnBar {
  export type Action = {
    /** Passed back to `onAction`; the component never interprets it. */
    id: string;
    /** The accessible name and the hover tooltip. */
    label: string;
    /** Raw SVG — the caller owns its own verbs. */
    icon: string;
  };

  /** One action the turn took. No mark and no colour: the tone is a STATUS,
   * and which hue or glyph stands for it is this skin's business. */
  export type Face = {
    id: string;
    tone: "pending" | "running" | "done" | "failed" | "declined";
    label: string;
  };

  export type Props = {
    actions?: Action[];
    /** Disclosed in the details tooltip. Attribution lives here. */
    meta?: { key: string; value: string }[];
    /** What the turn DID, when the composition chose not to spend a row on
     * each one. Empty in the density that shows every call — the pile is the
     * substitute for those rows, never a duplicate of them. */
    faces?: Face[];
    onAction?: (id: string) => void;
    onFaces?: () => void;
  };
}
